import {
  type WsPush,
  type WsPushChannel,
  type WsPushMessage,
  WebSocketResponse,
  WS_METHODS,
  type WsResponse as WsResponseMessage,
  WsResponse as WsResponseSchema,
} from "@taskin-code/contracts";
import { decodeUnknownJsonResult, formatSchemaError } from "@taskin-code/shared/schemaJson";
import { Result, Schema } from "effect";

type PushListener<C extends WsPushChannel> = (message: WsPushMessage<C>) => void;

interface PendingRequest {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout> | null;
}

interface SubscribeOptions {
  readonly replayLatest?: boolean;
}

interface RequestOptions {
  readonly timeoutMs?: number | null;
}

export type WsTransportState = "connecting" | "open" | "reconnecting" | "closed" | "disposed";

type TransportState = WsTransportState;

type StateListener = (state: TransportState) => void;

interface QueuedMessage {
  readonly id: string;
  readonly encoded: string;
}

const REQUEST_TIMEOUT_MS = 60_000;
const RECONNECT_DELAYS_MS = [500, 1_000, 2_000, 4_000, 8_000];
/**
 * Budget for a liveness probe. Deliberately short: a probe only runs when we
 * already suspect the socket, and the user is waiting on the answer.
 */
const PROBE_TIMEOUT_MS = 5_000;
/** How often to probe while the document is visible. */
const HEARTBEAT_INTERVAL_MS = 30_000;
const decodeWsResponse = decodeUnknownJsonResult(WsResponseSchema);
const isWebSocketResponseEnvelope = Schema.is(WebSocketResponse);

const isWsPushMessage = (value: WsResponseMessage): value is WsPush =>
  "type" in value && value.type === "push";

interface WsRequestEnvelope {
  id: string;
  body: {
    _tag: string;
    [key: string]: unknown;
  };
}

function asError(value: unknown, fallback: string): Error {
  if (value instanceof Error) {
    return value;
  }
  return new Error(fallback);
}

export class WsTransport {
  private ws: WebSocket | null = null;
  private nextId = 1;
  private readonly pending = new Map<string, PendingRequest>();
  private readonly listeners = new Map<string, Set<(message: WsPush) => void>>();
  private readonly latestPushByChannel = new Map<string, WsPush>();
  private readonly outboundQueue: QueuedMessage[] = [];
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  private state: TransportState = "connecting";
  private readonly url: string;
  private readonly stateListeners = new Set<StateListener>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private probeInFlight = false;
  private removeLifecycleListeners: (() => void) | null = null;

  constructor(url?: string) {
    const bridgeUrl = window.desktopBridge?.getWsUrl();
    const envUrl = import.meta.env.VITE_WS_URL as string | undefined;
    this.url =
      url ??
      (bridgeUrl && bridgeUrl.length > 0
        ? bridgeUrl
        : envUrl && envUrl.length > 0
          ? envUrl
          : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.hostname}:${window.location.port}/ws`);
    this.installLifecycleListeners();
    this.connect();
  }

  async request<T = unknown>(
    method: string,
    params?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    if (typeof method !== "string" || method.length === 0) {
      throw new Error("Request method is required");
    }

    const id = String(this.nextId++);
    const body = params != null ? { ...params, _tag: method } : { _tag: method };
    const message: WsRequestEnvelope = { id, body };
    const encoded = JSON.stringify(message);

    return new Promise<T>((resolve, reject) => {
      const timeoutMs = options?.timeoutMs === undefined ? REQUEST_TIMEOUT_MS : options.timeoutMs;
      const timeout =
        timeoutMs === null
          ? null
          : setTimeout(() => {
              this.pending.delete(id);
              reject(new Error(`Request timed out: ${method}`));
            }, timeoutMs);

      this.pending.set(id, {
        resolve: resolve as (result: unknown) => void,
        reject,
        timeout,
      });

      this.send(id, encoded);
    });
  }

  subscribe<C extends WsPushChannel>(
    channel: C,
    listener: PushListener<C>,
    options?: SubscribeOptions,
  ): () => void {
    let channelListeners = this.listeners.get(channel);
    if (!channelListeners) {
      channelListeners = new Set<(message: WsPush) => void>();
      this.listeners.set(channel, channelListeners);
    }

    const wrappedListener = (message: WsPush) => {
      listener(message as WsPushMessage<C>);
    };
    channelListeners.add(wrappedListener);

    if (options?.replayLatest) {
      const latest = this.latestPushByChannel.get(channel);
      if (latest) {
        wrappedListener(latest);
      }
    }

    return () => {
      channelListeners?.delete(wrappedListener);
      if (channelListeners?.size === 0) {
        this.listeners.delete(channel);
      }
    };
  }

  getLatestPush<C extends WsPushChannel>(channel: C): WsPushMessage<C> | null {
    const latest = this.latestPushByChannel.get(channel);
    return latest ? (latest as WsPushMessage<C>) : null;
  }

  getState(): TransportState {
    return this.state;
  }

  /**
   * Observe connection state. Returns an unsubscribe function. The listener is
   * called immediately with the current state so subscribers never render a
   * stale value on mount.
   */
  onStateChange(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  private setState(next: TransportState) {
    if (this.state === next) {
      return;
    }
    this.state = next;
    for (const listener of this.stateListeners) {
      try {
        listener(next);
      } catch {
        // Swallow listener errors
      }
    }
  }

  /**
   * Mobile browsers freeze a backgrounded page instead of closing its sockets,
   * so returning to the foreground can find a socket that reports OPEN while
   * the connection underneath is long gone. Nothing surfaces that on its own:
   * without an active check, the first request after unlocking the screen just
   * hangs until it times out a minute later.
   */
  private installLifecycleListeners() {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        // Probing a hidden page wastes battery and the answer cannot be
        // trusted anyway — timers are throttled while backgrounded.
        this.stopHeartbeat();
        return;
      }
      this.resumeNow();
    };
    const onOnline = () => {
      this.resumeNow();
    };
    const onOffline = () => {
      this.stopHeartbeat();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    // Safari restores pages from the back/forward cache without firing
    // visibilitychange, so bfcache restores need their own hook.
    window.addEventListener("pageshow", onVisibilityChange);

    this.removeLifecycleListeners = () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("pageshow", onVisibilityChange);
    };
  }

  /**
   * Reconnect without waiting out the backoff. Called when the user comes back
   * to the page: they are looking at the screen, so an 8-second wait reads as a
   * broken app.
   */
  private resumeNow() {
    if (this.disposed) {
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.startHeartbeat();
      void this.probeConnection();
      return;
    }

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // A pending connect attempt may itself be stuck against a dead network.
    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    this.reconnectAttempt = 0;
    this.connect();
  }

  /**
   * Ask the server to confirm the socket is alive. A zombie socket accepts
   * `send()` silently, so the timeout is the only signal — on which we drop the
   * socket and let the normal reconnect path take over.
   */
  private async probeConnection(): Promise<void> {
    if (this.probeInFlight || this.disposed || this.ws?.readyState !== WebSocket.OPEN) {
      return;
    }
    this.probeInFlight = true;
    const probed = this.ws;
    try {
      await this.request(WS_METHODS.serverPing, undefined, { timeoutMs: PROBE_TIMEOUT_MS });
    } catch {
      // Only tear down the socket we actually probed; a reconnect may have
      // already swapped it out while the probe was in flight.
      if (this.ws === probed) {
        probed.close();
      }
    } finally {
      this.probeInFlight = false;
    }
  }

  private startHeartbeat() {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return;
    }
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      void this.probeConnection();
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  dispose() {
    this.disposed = true;
    this.setState("disposed");
    this.stopHeartbeat();
    this.removeLifecycleListeners?.();
    this.removeLifecycleListeners = null;
    this.stateListeners.clear();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    for (const pending of this.pending.values()) {
      if (pending.timeout !== null) {
        clearTimeout(pending.timeout);
      }
      pending.reject(new Error("Transport disposed"));
    }
    this.pending.clear();
    this.outboundQueue.length = 0;
    this.ws?.close();
    this.ws = null;
  }

  private connect() {
    if (this.disposed) {
      return;
    }

    this.setState(this.reconnectAttempt > 0 ? "reconnecting" : "connecting");
    const ws = new WebSocket(this.url);

    ws.addEventListener("open", () => {
      this.ws = ws;
      this.reconnectAttempt = 0;
      this.setState("open");
      this.flushQueue();
      this.startHeartbeat();
    });

    ws.addEventListener("message", (event) => {
      this.handleMessage(event.data);
    });

    ws.addEventListener("close", () => {
      if (this.ws === ws) {
        this.ws = null;
        this.stopHeartbeat();
        // Messages still queued never reached the socket, so they are safe to
        // retry once we reconnect — and their callers keep waiting rather than
        // seeing a failure for something the server never saw. Only requests
        // already in flight are rejected: the server may well have run them,
        // so silently resending would risk executing a command twice.
        const queuedIds = new Set(this.outboundQueue.map((message) => message.id));
        for (const [id, pending] of this.pending.entries()) {
          if (queuedIds.has(id)) {
            continue;
          }
          if (pending.timeout !== null) {
            clearTimeout(pending.timeout);
          }
          this.pending.delete(id);
          pending.reject(new Error("WebSocket connection closed."));
        }
      }
      if (this.disposed) {
        this.setState("disposed");
        return;
      }
      this.setState("closed");
      this.scheduleReconnect();
    });

    ws.addEventListener("error", (event) => {
      // Log WebSocket errors for debugging (close event will follow)
      console.warn("WebSocket connection error", { type: event.type, url: this.url });
    });
  }

  private handleMessage(raw: unknown) {
    const result = decodeWsResponse(raw);
    if (Result.isFailure(result)) {
      console.warn("Dropped inbound WebSocket envelope", formatSchemaError(result.failure));
      return;
    }

    const message = result.success;
    if (isWsPushMessage(message)) {
      this.latestPushByChannel.set(message.channel, message);
      const channelListeners = this.listeners.get(message.channel);
      if (channelListeners) {
        for (const listener of channelListeners) {
          try {
            listener(message);
          } catch {
            // Swallow listener errors
          }
        }
      }
      return;
    }

    if (!isWebSocketResponseEnvelope(message)) {
      return;
    }

    const pending = this.pending.get(message.id);
    if (!pending) {
      return;
    }

    if (pending.timeout !== null) {
      clearTimeout(pending.timeout);
    }
    this.pending.delete(message.id);

    if (message.error) {
      pending.reject(new Error(message.error.message));
      return;
    }

    pending.resolve(message.result);
  }

  private send(id: string, encodedMessage: string) {
    if (this.disposed) {
      return;
    }

    this.outboundQueue.push({ id, encoded: encodedMessage });
    try {
      this.flushQueue();
    } catch {
      // Swallow: flushQueue has queued the message for retry on reconnect
    }
  }

  private flushQueue() {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      return;
    }

    while (this.outboundQueue.length > 0) {
      const message = this.outboundQueue.shift();
      if (!message) {
        continue;
      }
      try {
        this.ws.send(message.encoded);
      } catch (error) {
        this.outboundQueue.unshift(message);
        throw asError(error, "Failed to send WebSocket request.");
      }
    }
  }

  private scheduleReconnect() {
    if (this.disposed || this.reconnectTimer !== null) {
      return;
    }

    const delay =
      RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)] ??
      RECONNECT_DELAYS_MS[0]!;

    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
